/**
 * Calculator Component
 * Handles cloud provider points calculation with offers system
 */

class Calculator {
  constructor(element) {
    this.element = element;
    this.providerSelect = element.querySelector('[data-calculator-provider]');
    this.amountInput = element.querySelector('[data-calculator-amount]');
    this.calculateBtn = element.querySelector('[data-calculator-btn]');
    this.resultsContainer = element.querySelector('[data-calculator-results]');
    this.resultsTitle = element.querySelector('[data-results-title]');
    this.monthlyValue = element.querySelector('[data-monthly-value]');
    this.annualValue = element.querySelector('[data-annual-value]');
    this.biennialValue = element.querySelector('[data-biennial-value]');
    
    // Modal elements
    this.modal = document.querySelector('[data-calculator-modal]');
    this.modalClose = this.modal?.querySelectorAll('[data-modal-close]');
    this.modalOffers = this.modal?.querySelector('[data-modal-offers]');
    this.newsletterForm = this.modal?.querySelector('[data-newsletter-form]');
    this.formMessage = this.modal?.querySelector('[data-form-message]');
    
    // Get offers data
    this.offersData = this.getOffersData();
    
    this.init();
  }

  init() {
    this.calculateBtn.addEventListener('click', () => this.calculate());
    this.amountInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.calculate();
      }
    });

    // Clear modal history when provider changes
    this.providerSelect.addEventListener('change', () => {
      this.clearModalHistory();
    });

    // Modal close handlers
    if (this.modalClose) {
      this.modalClose.forEach(btn => {
        btn.addEventListener('click', () => this.closeModal());
      });
    }

    // Close modal on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal && this.modal.style.display !== 'none') {
        this.closeModal();
      }
    });
  }

  clearModalHistory() {
    // Clear all calculator modal entries from sessionStorage
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('calculator_modal_')) {
        sessionStorage.removeItem(key);
      }
    });
  }

  getOffersData() {
    const offersScript = document.querySelector('[data-calculator-offers]');
    if (offersScript) {
      try {
        return JSON.parse(offersScript.textContent);
      } catch (e) {
        console.error('Error parsing offers data:', e);
        return [];
      }
    }
    return [];
  }

  calculate() {
    const selectedOption = this.providerSelect.options[this.providerSelect.selectedIndex];
    const provider = selectedOption.value;
    const providerName = selectedOption.text.split(' - ')[0];
    const pointsPerDollar = parseFloat(selectedOption.dataset.points) || 1;
    const amount = parseFloat(this.amountInput.value) || 0;

    if (amount <= 0) {
      this.showError('Please enter a valid amount');
      return;
    }

    // Calculate points
    const monthlyPoints = Math.floor(amount * pointsPerDollar);
    const annualPoints = monthlyPoints * 12;
    const biennialPoints = monthlyPoints * 24;

    // Update results
    this.resultsTitle.textContent = `Your Points with ${providerName}`;
    this.updateValue(this.monthlyValue, monthlyPoints);
    this.updateValue(this.annualValue, annualPoints);
    this.updateValue(this.biennialValue, biennialPoints);

    // Show results with animation
    this.resultsContainer.classList.add('active');
    
    // Scroll to results
    setTimeout(() => {
      this.resultsContainer.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'nearest' 
      });
    }, 100);

    // Check for applicable offers
    const applicableOffers = this.getApplicableOffers(monthlyPoints, provider);
    if (applicableOffers.length > 0) {
      setTimeout(() => {
        this.showOffersModal(applicableOffers, monthlyPoints, providerName);
      }, 600);
    }
  }

  updateValue(element, value) {
    element.classList.add('updating');
    element.textContent = this.formatNumber(value);
    setTimeout(() => {
      element.classList.remove('updating');
    }, 500);
  }

  formatNumber(num) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  showError(message) {
    this.amountInput.classList.add('error');
    alert(message);
    setTimeout(() => {
      this.amountInput.classList.remove('error');
    }, 3000);
  }

  getApplicableOffers(monthlyPoints, provider) {
    console.log('Getting offers for:', { monthlyPoints, provider, totalOffers: this.offersData.length });
    
    const filtered = this.offersData.filter(offer => {
      // Check if points threshold is met
      const meetsPoints = monthlyPoints >= offer.min_points;
      
      // Check if offer applies to selected provider
      let meetsProvider = false;
      
      if (offer.providers === 'all') {
        // Offer applies to all providers
        meetsProvider = true;
      } else {
        // Check if provider is in the comma-separated list
        const providersList = offer.providers.split(',');
        meetsProvider = providersList.includes(provider);
      }
      
      console.log('Offer:', offer.title, {
        min_points: offer.min_points,
        meetsPoints,
        providers: offer.providers,
        meetsProvider,
        passes: meetsPoints && meetsProvider
      });
      
      return meetsPoints && meetsProvider;
    }).sort((a, b) => b.min_points - a.min_points);
    
    console.log('Applicable offers:', filtered.length);
    return filtered;
  }

  showOffersModal(offers, points, provider) {
    if (!this.modal) {
      console.log('Modal element not found');
      return;
    }

    // Check if there are any offers to show
    if (!offers || offers.length === 0) {
      console.log('No offers to show');
      return;
    }

    console.log('Showing modal with', offers.length, 'offers for', points, 'points on', provider);

    // Create a unique key based on offers, points, and provider
    const offerIds = offers.map(o => o.id).join(',');
    const sessionKey = `calculator_modal_${provider}_${offerIds}_${points}`;
    
    // Check if this exact combination was shown recently (within last 30 seconds)
    const lastShown = sessionStorage.getItem(sessionKey);
    const now = Date.now();
    
    if (lastShown && (now - parseInt(lastShown)) < 30000) {
      console.log('Modal was shown recently, skipping');
      return;
    }

    // Mark as shown with timestamp
    sessionStorage.setItem(sessionKey, now.toString());

    // Get first offer for hero section
    const mainOffer = offers[0];
    
    // Populate hero section
    const modalHero = this.modal.querySelector('[data-modal-hero]');
    if (modalHero && mainOffer) {
      const badgeText = this.element.closest('section').querySelector('[data-modal-badge]')?.textContent || '🏆 Exclusive Offer';
      modalHero.innerHTML = this.createHeroSection(mainOffer, points, provider, badgeText);
    }

    // Populate offers as bullet list
    this.modalOffers.innerHTML = '';
    offers.forEach(offer => {
      const offerEl = this.createOfferListItem(offer);
      this.modalOffers.appendChild(offerEl);
    });

    // Show modal
    this.modal.style.display = 'block';
    document.body.style.overflow = 'hidden';

    console.log('Modal displayed successfully');

    // Track event (if you have analytics)
    if (typeof gtag !== 'undefined') {
      gtag('event', 'calculator_offers_shown', {
        'points': points,
        'provider': provider,
        'offers_count': offers.length
      });
    }
  }

  createHeroSection(offer, points, provider, badgeText) {
    return `
      <div class="m-calculator-modal__hero-badge">
        ${badgeText}
      </div>
      <h2 class="m-calculator-modal__hero-title">
        ${offer.title}
      </h2>
      <p class="m-calculator-modal__hero-subtitle">
        ${offer.subtitle || offer.description}
      </p>
    `;
  }

  createOfferListItem(offer) {
    const li = document.createElement('li');
    li.className = 'm-calculator-modal__offer-item';
    li.textContent = offer.title;
    return li;
  }

  createOfferElement(offer) {
    const div = document.createElement('div');
    div.className = 'm-calculator-modal__offer';
    
    let imageHTML = '';
    if (offer.image) {
      imageHTML = `
        <div class="m-calculator-modal__offer-image">
          <img src="${offer.image}" alt="${offer.title}" loading="lazy">
        </div>
      `;
    }

    let ctaHTML = '';
    if (offer.cta_text && offer.cta_url) {
      ctaHTML = `
        <a href="${offer.cta_url}" class="m-calculator-modal__offer-cta">
          ${offer.cta_text} →
        </a>
      `;
    }

    div.innerHTML = `
      ${imageHTML}
      <div class="m-calculator-modal__offer-content">
        <div class="m-calculator-modal__offer-header">
          <h4 class="m-calculator-modal__offer-title">${offer.title}</h4>
          ${offer.badge ? `<span class="m-calculator-modal__offer-badge" style="background: ${offer.badge_color}">${offer.badge}</span>` : ''}
        </div>
        <p class="m-calculator-modal__offer-description">${offer.description}</p>
        ${ctaHTML}
      </div>
    `;

    return div;
  }

  closeModal() {
    if (!this.modal) return;
    
    this.modal.style.display = 'none';
    document.body.style.overflow = '';
    
    // Reset form if exists
    if (this.newsletterForm) {
      this.newsletterForm.reset();
      this.formMessage.textContent = '';
      this.formMessage.className = 'm-calculator-modal__form-message';
    }
  }

  async handleNewsletter(e) {
    e.preventDefault();
    
    const emailInput = this.newsletterForm.querySelector('[data-newsletter-email]');
    const email = emailInput.value;
    const submitBtn = this.newsletterForm.querySelector('.m-calculator-modal__submit');
    
    // Disable form
    submitBtn.disabled = true;
    submitBtn.textContent = 'Subscribing...';
    
    try {
      // Replace this with your actual newsletter API endpoint
      const response = await fetch('/contact#contact_form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          'contact[email]': email,
          'contact[tags]': 'newsletter,calculator',
          'form_type': 'customer'
        })
      });

      if (response.ok) {
        this.formMessage.textContent = 'Thank you for subscribing! Check your email for confirmation.';
        this.formMessage.className = 'm-calculator-modal__form-message success';
        this.newsletterForm.reset();
        
        // Track event
        if (typeof gtag !== 'undefined') {
          gtag('event', 'newsletter_subscribe', {
            'method': 'calculator_modal'
          });
        }
      } else {
        throw new Error('Subscription failed');
      }
    } catch (error) {
      this.formMessage.textContent = 'Something went wrong. Please try again.';
      this.formMessage.className = 'm-calculator-modal__form-message error';
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Subscribe';
    }
  }
}

// Initialize all calculators on page
document.addEventListener('DOMContentLoaded', () => {
  const calculators = document.querySelectorAll('[data-calculator]');
  calculators.forEach(calculator => {
    new Calculator(calculator);
  });
});

// Handle Shopify section events
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const calculator = event.target.querySelector('[data-calculator]');
    if (calculator) {
      new Calculator(calculator);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    // Cleanup if needed
    const modal = event.target.querySelector('[data-calculator-modal]');
    if (modal && modal.style.display !== 'none') {
      modal.style.display = 'none';
      document.body.style.overflow = '';
    }
  });
}
